import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useRecoilValue } from "recoil";
import { useCmsPreviewLang } from "../../../hooks/useCmsPreviewLang";
import styled from "styled-components";
import { apiSDKService } from "../../../api/renewal/api.service";
import useDisplay from "../../../hooks/useDisplay";
import { homeContentFallback } from "../../../lang/renewal/homeLang";
import { useCmsContent } from "../../../hooks/useCmsContent";
import {
  adaptWeDo,
  adaptConstellation,
  adaptWeService,
} from "./_utils/adaptCmsContent";
import { langState } from "../../../store/langState";
import Constellation from "./constellation/Constellation";
import Device from "./device/Device";
import Intro from "./intro/Intro3";
import MainVideo from "./MainVideo";
import News from "./News";
import TransparentPopup from "./popup/TransparentPopup";
import Recruit from "./Recruit";
import SardipCard from "./sardipCard/SardipCard";
import WhatWeDo from "./weDo/WhatWeDo";
import WeService from "./WeService";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// 어드민 미리보기 "이동" 버튼(CMS_SCROLL_TO)이 찾는 앵커 (높이 0, 레이아웃 영향 없음)
const anchor = (id) => (
  <div key={id} id={id} aria-hidden style={{ scrollMarginTop: "80px" }} />
);

const HomeRenewal = () => {
  const { isDesktop, isMobile, isIPadLandscape } = useDisplay();

  const langValue = useRecoilValue(langState);
  useCmsPreviewLang(); // 어드민 미리보기 언어 전환 수신

  // S3 JSON에서 콘텐츠 fetch (env 미설정 시 homeLang.js fallback 사용)
  const cmsContent = useCmsContent("home", homeContentFallback);
  const { mainVideo, intro } = cmsContent;
  // CMS(필드우선) → 컴포넌트(언어우선) 어댑터 + homeLang 정적자산 병합
  const weDo = useMemo(() => adaptWeDo(cmsContent.weDo), [cmsContent.weDo]);
  const constellation = useMemo(
    () => adaptConstellation(cmsContent.constellation),
    [cmsContent.constellation]
  );
  const weService = useMemo(
    () => adaptWeService(cmsContent.weService),
    [cmsContent.weService]
  );
  const containerRef = useRef(null);
  const sectionRefs = useRef([]);
  const deviceTargetRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [popupList, setPopupList] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [cookies, setCookie] = useCookies(["PopupCookie"]);

  useEffect(() => {
    const getPopup = async () => {
      try {
        /*
      NoticeResponseDTO {
        kr: {
          alt: string;
          image: {
            mobile: string;
            pc: string;
          };
      }[];
        en: {
          alt: string;
          image: {
            mobile: string;
            pc: string;
          };
      }[];
      */
        const popupData = await apiSDKService.getNotices(1, 5, langValue);
        let validPopupData = [];

        // 유효한 팝업 데이터만 필터링
        const targetPopupData =
          langValue === "KR" ? popupData?.kr : popupData?.en;
        validPopupData = Array.isArray(targetPopupData)
          ? targetPopupData.filter(
              (popup) =>
                popup &&
                popup.image &&
                popup.alt &&
                (popup.image.mobile || popup.image.pc)
            )
          : [];

        setPopupList(validPopupData);

        const popupCookie = cookies["PopupCookie"];
        const currentDate = new Date();

        if (
          validPopupData.length > 0 &&
          (!popupCookie || new Date(popupCookie) < currentDate)
        ) {
          setPopupOpen(true);
        }
      } catch (error) {
        console.error("팝업 데이터를 가져오는 중 오류 발생:", error);
        setPopupList([]);
        setPopupOpen(false);
      }
    };

    const savedScrollPosition = sessionStorage.getItem("scrollPosition");

    if (savedScrollPosition !== null) {
      setIsLoading(true);
      window.scrollTo(0, parseInt(savedScrollPosition, 10));
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }

    const handleBeforeUnload = () => {
      sessionStorage.setItem("scrollPosition", window.scrollY.toString());
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    getPopup();

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [cookies, setCookie, setPopupList, setPopupOpen, langValue]);

  const handleClose = () => {
    setPopupOpen(false);
    console.log("팝업이 닫혔습니다.");
  };

  const handleDontShowToday = () => {
    const expires = new Date();
    expires.setDate(expires.getDate() + 1);
    expires.setHours(0, 0, 0, 0);

    setCookie("PopupCookie", expires.toISOString(), { path: "/", expires });
    setPopupOpen(false);
    console.log("오늘 하루 동안 팝업이 표시되지 않습니다.");
  };

  const sectionComponents = useMemo(
    () => [
      anchor("cms-hero"),
      <MainVideo
        key="video"
        fallbackImageSrc={mainVideo.fallbackImg}
        videoSrc={isDesktop ? mainVideo.pcVideo : mainVideo.mobileVideo}
        scrollTriggerTime={30}
        ref={(el) => (sectionRefs.current[0] = el)}
        isDesktop={isDesktop}
      />,
      anchor("cms-intro"),
      <Intro
        key="intro"
        ref={(el) => (sectionRefs.current[1] = el)}
        {...{ langValue, isDesktop, isMobile, isIPadLandscape, intro }}
      />,
      <Device
        key="device"
        ref={(el) => {
          sectionRefs.current[2] = el;
          if (el && el.children[1] && el.children[1].children[0]) {
            deviceTargetRef.current = el.children[1].children[0];
          }
        }}
        {...{ langValue }}
      />,
      anchor("cms-wedo"),
      <WhatWeDo key="whatwedo" {...{ langValue, weDo }} />,
      anchor("cms-constellation"),
      <Constellation key="constellation" {...{ langValue, constellation }} />,
      anchor("cms-service"),
      <WeService key="weservice" {...{ langValue, weService }} />,
      <SardipCard key="sardipcard" {...{ langValue }} />,
      <News key="news" {...{ langValue }} />,
      <Recruit key="recruit" {...{ langValue }} />,
    ],
    [
      isDesktop,
      langValue,
      isIPadLandscape,
      isMobile,
      mainVideo,
      intro,
      weDo,
      constellation,
      weService,
    ]
  );

  return (
    <>
      {isLoading && <Loading />}
      <Container ref={containerRef}>{sectionComponents}</Container>
      {popupOpen && Array.isArray(popupList) && popupList.length > 0 ? (
        <>
          <TransparentPopup
            contents={popupList}
            onClose={handleClose}
            onDontShowToday={handleDontShowToday}
          />
        </>
      ) : null}
    </>
  );
};

export default React.memo(HomeRenewal);

const Container = styled.div`
  scroll-behavior: smooth;
  font-size: 0.8vw !important;

  @media (max-width: 768px) {
    font-size: 100% !important;
  }

  @media (min-width: 768px) and (max-width: 1400px) {
    font-size: 110% !important;
  }
`;

const Loading = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;
